import { useState } from "react";
import { BUSINESS_TYPES } from "../data/businesses";
import { RED_PRINCIPAL_OPTIONS } from "../lib/perfil";
import { trackEvent } from "../lib/analytics";
import { Sparkles, Plus, Trash2, ArrowLeft, Save, User, Store, MapPin, Share2, Package } from "lucide-react";

// Curated MX cities (same list Screen 1 uses). Duplicated on purpose so we
// don't couple the setup screen to Screen 1's internals.
const CITIES = [
  "Acapulco", "Aguascalientes", "Cancún", "Chilpancingo", "Ciudad de México",
  "Culiacán", "Guadalajara", "Hermosillo", "León", "Mérida", "Mexicali",
  "Monterrey", "Morelia", "Oaxaca", "Puebla", "Querétaro", "Saltillo",
  "San Luis Potosí", "Tijuana", "Veracruz",
];
const OTHER_CITY = "Otra ciudad";

function deriveCityState(initial) {
  const v = (initial || "").trim();
  if (!v) return { sel: "", custom: "" };
  if (CITIES.includes(v)) return { sel: v, custom: "" };
  return { sel: OTHER_CITY, custom: v };
}

const emptyProducto = () => ({ nombre: "", precio: "", costo: "" });

export default function PerfilSetup({ initialPerfil, onSave, onCancel }) {
  const [nombreNegocio, setNombreNegocio] = useState(initialPerfil?.nombreNegocio || "");
  const [giro, setGiro] = useState(initialPerfil?.giro || "");
  const cityInit = deriveCityState(initialPerfil?.ciudad || "");
  const [citySel, setCitySel] = useState(cityInit.sel);
  const [cityCustom, setCityCustom] = useState(cityInit.custom);
  const [redPrincipal, setRedPrincipal] = useState(initialPerfil?.redPrincipal || "");
  const [productos, setProductos] = useState(
    initialPerfil?.productos?.length
      ? initialPerfil.productos.map((p) => ({
          nombre: p.nombre || "",
          precio: p.precio != null ? String(p.precio) : "",
          costo: p.costo != null ? String(p.costo) : "",
        }))
      : [emptyProducto()],
  );

  const isEdit = !!initialPerfil;
  const resolvedCity = citySel === OTHER_CITY ? cityCustom.trim() : citySel;

  const productosValidos = productos.every(
    (p) => p.nombre.trim() && Number(p.precio) > 0,
  );

  const canSave =
    nombreNegocio.trim().length >= 2 &&
    giro &&
    resolvedCity.length >= 2 &&
    redPrincipal &&
    productos.length >= 1 &&
    productos.length <= 10 &&
    productosValidos;

  const updateProducto = (idx, field, value) => {
    setProductos((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    );
  };

  const addProducto = () => {
    if (productos.length >= 10) return;
    setProductos((prev) => [...prev, emptyProducto()]);
  };

  const removeProducto = (idx) => {
    setProductos((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      nombreNegocio: nombreNegocio.trim(),
      giro,
      ciudad: resolvedCity,
      redPrincipal,
      productos: productos.map((p) => ({
        nombre: p.nombre.trim(),
        precio: Number(p.precio),
        ...(p.costo !== "" && !Number.isNaN(Number(p.costo)) && Number(p.costo) >= 0
          ? { costo: Number(p.costo) }
          : {}),
      })),
    });
  };

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-10 pb-10 animate-fade-in-up"
      data-testid="perfil-setup-screen"
    >
      {/* Brand + optional back button on edit mode */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-orange-500 text-white shadow-md shadow-orange-500/30">
            <Sparkles className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-bold tracking-tight text-slate-900">
            Nego<span className="text-orange-500">IA</span>
          </span>
        </div>
        {isEdit && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            data-testid="perfil-cancel-button"
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 active:text-slate-800"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
            Cancelar
          </button>
        )}
      </div>

      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight mt-6">
        {isEdit ? (
          <>Actualiza tu <span className="text-orange-500">negocio</span></>
        ) : (
          <>Configura tu <span className="text-orange-500">negocio</span></>
        )}
      </h1>
      <p className="text-base text-slate-500 mt-3 leading-relaxed">
        Solo lo haces una vez. La app lo recuerda siempre.
      </p>

      {/* 1. Nombre */}
      <div className="mt-8">
        <label
          htmlFor="perfil-nombre"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-2"
        >
          <Store className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          ¿Cómo se llama tu negocio?
        </label>
        <input
          id="perfil-nombre"
          type="text"
          value={nombreNegocio}
          onChange={(e) => setNombreNegocio(e.target.value)}
          placeholder="Ej: Taquería Doña Mago"
          maxLength={60}
          data-testid="perfil-nombre-input"
          className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-400"
        />
      </div>

      {/* 2. Giro */}
      <div className="mt-8">
        <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-3">
          <User className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          ¿Qué tipo de negocio es?
        </label>
        <div className="grid grid-cols-2 gap-3">
          {BUSINESS_TYPES.map((b) => {
            const Icon = b.icon;
            const isSelected = giro === b.id;
            return (
              <button
                key={b.id}
                type="button"
                data-testid={`perfil-giro-${b.id}`}
                onClick={() => {
                  setGiro(b.id);
                  trackEvent("selecciono_giro", { giro: b.id });
                }}
                className={[
                  "flex flex-col items-center justify-center aspect-square rounded-2xl border-2 p-3 transition-all active:scale-95",
                  isSelected
                    ? "border-orange-500 bg-orange-50 shadow-lg shadow-orange-500/20"
                    : "border-slate-200 bg-white active:border-slate-300",
                ].join(" ")}
              >
                <Icon
                  className={[
                    "w-7 h-7 mb-2 transition-colors",
                    isSelected ? "text-orange-500" : "text-teal-700",
                  ].join(" ")}
                  strokeWidth={1.8}
                />
                <span className={["text-xs font-semibold text-center leading-tight", isSelected ? "text-slate-900" : "text-slate-700"].join(" ")}>
                  {b.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Ciudad */}
      <div className="mt-8">
        <label
          htmlFor="perfil-city"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-2"
        >
          <MapPin className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          ¿En qué ciudad estás?
        </label>
        <select
          id="perfil-city"
          value={citySel}
          onChange={(e) => setCitySel(e.target.value)}
          data-testid="perfil-city-select"
          className="appearance-none w-full p-4 pr-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all"
        >
          <option value="" disabled>Selecciona tu ciudad</option>
          {CITIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
          <option value={OTHER_CITY}>{OTHER_CITY}</option>
        </select>
        {citySel === OTHER_CITY && (
          <input
            type="text"
            value={cityCustom}
            onChange={(e) => setCityCustom(e.target.value)}
            placeholder="Escribe tu ciudad"
            maxLength={60}
            data-testid="perfil-city-custom-input"
            className="mt-3 w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-400 animate-scale-in"
          />
        )}
      </div>

      {/* 4. Red principal */}
      <div className="mt-8">
        <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-3">
          <Share2 className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          ¿Cuál es tu red social principal?
        </label>
        <div className="grid grid-cols-2 gap-2.5">
          {RED_PRINCIPAL_OPTIONS.map((r) => {
            const isSelected = redPrincipal === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setRedPrincipal(r.id)}
                data-testid={`perfil-red-${r.id}`}
                className={[
                  "flex items-center justify-center py-3 rounded-xl border-2 text-sm font-semibold transition-all active:scale-[0.98]",
                  isSelected
                    ? "border-teal-600 bg-teal-50 text-teal-900"
                    : "border-slate-200 bg-white text-slate-700",
                ].join(" ")}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 5. Productos */}
      <div className="mt-8">
        <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-1">
          <Package className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          Tus productos o platillos
        </label>
        <p className="text-xs text-slate-500 mb-3">
          Mínimo 1, máximo 10. El costo es opcional pero mejora tu estrategia.
        </p>

        <div className="flex flex-col gap-3">
          {productos.map((p, idx) => (
            <div
              key={idx}
              className="p-3 rounded-xl border-2 border-slate-200 bg-slate-50"
              data-testid={`perfil-producto-row-${idx}`}
            >
              <input
                type="text"
                value={p.nombre}
                onChange={(e) => updateProducto(idx, "nombre", e.target.value)}
                placeholder="Nombre del producto"
                maxLength={40}
                data-testid={`perfil-producto-nombre-${idx}`}
                className="w-full p-3 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:border-teal-600 outline-none transition-all placeholder:text-slate-400"
              />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Precio $</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.5"
                    value={p.precio}
                    onChange={(e) => updateProducto(idx, "precio", e.target.value)}
                    placeholder="120"
                    data-testid={`perfil-producto-precio-${idx}`}
                    className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:border-teal-600 outline-none transition-all placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                    Costo $ <span className="text-slate-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.5"
                    value={p.costo}
                    onChange={(e) => updateProducto(idx, "costo", e.target.value)}
                    placeholder="40"
                    data-testid={`perfil-producto-costo-${idx}`}
                    className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:border-teal-600 outline-none transition-all placeholder:text-slate-400"
                  />
                </div>
              </div>
              {productos.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeProducto(idx)}
                  data-testid={`perfil-producto-remove-${idx}`}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-600 active:text-red-800"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                  Eliminar
                </button>
              )}
            </div>
          ))}
        </div>

        {productos.length < 10 && (
          <button
            type="button"
            onClick={addProducto}
            data-testid="perfil-add-producto-button"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-xl border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-600 active:bg-slate-50 active:border-slate-400 transition-all"
          >
            <Plus className="w-4 h-4" strokeWidth={2.4} />
            Agregar otro producto
          </button>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1 min-h-[24px]" />

      {/* Save */}
      <div className="mt-10 pb-safe">
        <button
          type="button"
          disabled={!canSave}
          onClick={handleSave}
          data-testid="perfil-save-button"
          className={[
            "w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold transition-all",
            canSave
              ? "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:scale-[0.98] active:bg-orange-600"
              : "bg-slate-100 text-slate-400 cursor-not-allowed",
          ].join(" ")}
        >
          <Save className="w-5 h-5" strokeWidth={2.5} />
          {isEdit ? "Guardar cambios →" : "Guardar mi negocio →"}
        </button>
        <p className="text-center text-xs text-slate-400 mt-3">
          Solo lo haces una vez. La app lo recuerda siempre.
        </p>
      </div>
    </div>
  );
}
